/* eslint-disable @typescript-eslint/no-explicit-any */
import apiRoutes from "@/src/apiRoutes";
import AuthInput from "@/src/components/AuthInput";
import { Button } from "@/src/components/Button/Button";
import PasswordInput from "@/src/components/PasswordInput";
import { useCountries } from "@/src/hooks/useCountries";
import api from "@/src/utils";
import { useMutation } from "@tanstack/react-query";
import { SubmitHandler, UseFormReturn } from "react-hook-form";
import va from "@vercel/analytics";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { authenticate } from "@/src/features/userSlice";
import { useAppDispatch } from "@/src/app/hooks";

interface Props {
  form: UseFormReturn<
    {
      email: string;
      code: string;
      country: string;
      surname: string;
      first_name: string;
      username: string;
      referral_code?: string | undefined;
      password: string;
      phone: string;
      source?: string | undefined;
    },
    any
  >;
}

const PersonalInfo = ({ form }: Props) => {
  const { countries } = useCountries();
  const dispatch = useAppDispatch();
  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
  } = form;
  const navigate = useNavigate();

  const country = watch("country");

  const selectedCode = countries?.info.find((item) => item?.name === country);

  const ua = navigator.userAgent;

  const signin = async () => {
    const res = await api.post(apiRoutes.login, {
      username: form.watch().username,
      password: form.watch().password,
      ua,
    });
    return res.data;
  };

  const { isLoading: loginLoad, mutate: mutateLogin } = useMutation({
    mutationFn: signin,
    onSuccess: (data) => {
      if (data.success) {
        va.track("login");
        dispatch(authenticate(data.info));
        toast.success("Successfully logged in");
        navigate("/app/home");
      }
    },
  });

  const submitRegister = async () => {
    const res = await api.post(apiRoutes.signup, {
      ...form.watch(),
      country: countries?.info.find(
        (item) => item?.name === form.watch("country")
      )?.id,
      phone: `+${selectedCode?.short_code_iso3}${form.watch("phone")}`,
    });

    return res?.data;
    // }
  };

  const { mutate, isLoading } = useMutation({
    mutationFn: submitRegister,
    onSuccess: (data) => {
      if (data.success) {
        va.track("Register");
        toast.success(data?.info);
        mutateLogin();
        // navigate("/");
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const onSubmit: SubmitHandler<any> = async () => {
    mutate();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3">
      <AuthInput
        label="Phone Number"
        type="number"
        left={
          <div className="flex items-center gap-2 justify-center h-full pl-5">
            <img src={selectedCode?.image} className="h-4 w-5" alt="country" />{" "}
            <span className="text-sm">+{selectedCode?.short_code_iso3}</span>
          </div>
        }
        {...register("phone")}
        errors={errors.username?.message}
      />
      <AuthInput
        label="Username"
        {...register("username")}
        errors={errors.username?.message}
      />
      <div className="grid md:grid-cols-2 gap-3">
        <AuthInput
          label="First Name"
          {...register("first_name")}
          errors={errors.first_name?.message}
        />
        <AuthInput
          label="Last Name"
          {...register("surname")}
          errors={errors.surname?.message}
        />
      </div>
      <PasswordInput
        label="Password"
        {...register("password")}
        errors={errors.password?.message}
      />
      <Button
        type={"submit"}
        loading={isLoading || loginLoad}
        variant={"primary"}
        className="md:py-4 py-3 rounded-xl md:mt-5"
      >
        Sign up
      </Button>
    </form>
  );
};

export default PersonalInfo;
